// src/pages/ResetPassword.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useSearchParams, useNavigate } from 'react-router-dom';

/* ---------------- Password strength (0..4) ---------------- */
function analyzeStrength(pwd, email = '') {
  const tips = [];
  if (!pwd) return { score: 0, label: 'Empty', tips: ['Enter a password'] };

  const len = pwd.length;
  const sets = {
    lower: /[a-z]/.test(pwd),
    upper: /[A-Z]/.test(pwd),
    digit: /\d/.test(pwd),
    symbol: /[^A-Za-z0-9]/.test(pwd),
  };
  const diversity = Object.values(sets).filter(Boolean).length;

  // Simple dictionary / pattern checks (no extra libs)
  const common = [
    'password','passw0rd','qwerty','asdfgh','letmein','welcome',
    'admin','shop123','admin123','123456','12345678','iloveyou',
  ];
  const looksCommon = common.some(c => pwd.toLowerCase().includes(c));
  const repeats = /(.)\1{2,}/.test(pwd);                // aaa, 1111
  const seqAsc = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const seqDesc = seqAsc.split('').reverse().join('');
  const looksSequential =
    seqAsc.includes(pwd.toLowerCase()) ||
    seqDesc.includes(pwd.toLowerCase()) ||
    /(?:(?:abc|bcd|cde|123|234|345))/i.test(pwd);
  const emailPart = (email || '').split('@')[0] || '';
  const containsEmail = emailPart && pwd.toLowerCase().includes(emailPart.toLowerCase());

  let score = 0;

  // Base on length
  if (len >= 8) score++;
  if (len >= 10) score++;
  if (len >= 14) score++;

  // Diversity
  if (diversity >= 2) score++;
  if (diversity >= 3) score++;

  // Penalties
  if (looksCommon) score -= 2;
  if (repeats) score -= 1;
  if (looksSequential) score -= 1;
  if (containsEmail) score -= 1;
  score = Math.max(0, Math.min(4, score));

  // Tips
  if (len < 12) tips.push('Use 12+ characters');
  if (diversity < 3) tips.push('Mix upper/lower, numbers, symbols');
  if (looksCommon) tips.push('Avoid common words or “123456” patterns');
  if (repeats) tips.push('Avoid repeated characters like “aaa”');
  if (looksSequential) tips.push('Avoid sequences like “abcd” or “1234”');
  if (containsEmail) tips.push('Don’t include your email/username');

  const label = ['Very weak','Weak','Fair','Good','Strong'][score];
  return { score, label, tips };
}

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();

  const [token, setToken] = useState('');
  const tenant = sp.get('tenant') || '';
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [done, setDone] = useState(false);
  const [emailHint] = useState(sp.get('email') || '');

  // --- same logo behavior as Login.jsx ---
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => { setToken(sp.get('token') || ''); }, [sp]);

  const strength = useMemo(() => analyzeStrength(pwd1, emailHint), [pwd1, emailHint]);
  const matchErr = useMemo(() => (pwd2 && pwd1 !== pwd2 ? 'Passwords do not match' : ''), [pwd1, pwd2]);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!token) return setErr('Missing token — use the email link');
    if (pwd1.length < 8) return setErr('Password must be at least 8 characters');
    if (pwd1 !== pwd2) return setErr('Passwords do not match');

    setSaving(true);
    try {
      await api.post('/auth/reset', { token, newPassword: pwd1, tenant }, { headers: { 'X-Tenant-ID': tenant } });
      setDone(true);
      setTimeout(() => nav('/login'), 1800);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card" role="dialog" aria-labelledby="rpTitle">
        {/* unified company logo (public/brand/logo.png) — identical to Login.jsx */}
        {!logoBroken ? (
          <img
            src="/brand/logo.png"
            alt="Company logo"
            className="brand-logo"
            onError={() => setLogoBroken(true)}
            draggable="false"
          />
        ) : (
          <div className="logo-fallback" aria-hidden="true" />
        )}

        {!done ? (
          <>
            <h2 id="rpTitle" className="title">Choose a new password</h2>
            <p className="muted" style={{ marginTop: 4 }}>
              For your account security, pick a strong password (min 8 characters).
            </p>

            {err && <div className="alert">{err}</div>}

            <form onSubmit={submit} className="form">
              {/* New password */}
              <label className="field">
                <span className="label">New Password</span>
                <div className="pwd">
                  <input
                    className="input"
                    type={showPwd1 ? 'text' : 'password'}
                    value={pwd1}
                    onChange={(e)=>setPwd1(e.target.value)}
                    disabled={saving}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="eye"
                    onClick={()=>setShowPwd1(s=>!s)}
                    aria-label={showPwd1 ? 'Hide password' : 'Show password'}
                    disabled={saving}
                  >
                    {showPwd1 ? '🙈' : '👁️'}
                  </button>
                </div>

                {/* Strength meter */}
                <StrengthMeter score={strength.score} label={strength.label} />
                {pwd1 && strength.tips.length > 0 && (
                  <ul className="tips">
                    {strength.tips.slice(0, 3).map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                )}
              </label>

              {/* Confirm */}
              <label className="field">
                <span className="label">Confirm Password</span>
                <div className="pwd">
                  <input
                    className="input"
                    type={showPwd2 ? 'text' : 'password'}
                    value={pwd2}
                    onChange={(e)=>setPwd2(e.target.value)}
                    disabled={saving}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="eye"
                    onClick={()=>setShowPwd2(s=>!s)}
                    aria-label={showPwd2 ? 'Hide password' : 'Show password'}
                    disabled={saving}
                  >
                    {showPwd2 ? '🙈' : '👁️'}
                  </button>
                </div>
                {matchErr && <div className="inline-err">{matchErr}</div>}
              </label>

              <button
                className="btn primary"
                type="submit"
                disabled={saving || !token}
                title={!token ? 'Missing token' : undefined}
              >
                {saving ? 'Saving…' : 'Set new password'}
              </button>
            </form>
          </>
        ) : (
          <div className="success">
            <h2 className="title" style={{ marginBottom: 6 }}>Password updated</h2>
            <p className="muted">Redirecting you to login…</p>
          </div>
        )}
      </div>

      <style>{`
        /* ——— Match Login.jsx container behavior ——— */
        .auth-wrap {
          min-height: calc(100dvh - 64px);
          display: grid;
          place-items: center;
          padding: 16px;
          overflow: hidden;
          background: transparent; /* lets wallpaper show */
          color: var(--text, #0f172a);
        }
        @media (min-width: 640px) { .auth-wrap { padding: 24px; } }

        .auth-card {
          width: 100%;
          max-width: 420px;
          background: var(--bg-elev, #fff);
          border: 1px solid var(--hairline, #e5e7eb);
          border-radius: 16px;
          box-shadow: var(--shadow, 0 10px 30px rgba(2,6,23,.08));
          padding: 20px;
          box-sizing: border-box;
        }

        /* === same brand logo styles as Login.jsx === */
        .brand-logo {
          display: block;
          width: 200px;
          height: 200px;
          object-fit: contain;
          margin: 0 auto 16px;
          border-radius: 28px;
          user-select: none;
          pointer-events: none;
          background: transparent;
          border: none;
          box-shadow: none;
          filter: none;
        }
        .logo-fallback {
          width: 64px; height: 64px; border-radius: 14px; margin: 0 auto 12px;
          background: radial-gradient(80% 80% at 30% 20%, #93c5fd, #60a5fa);
          box-shadow: 0 6px 18px rgba(37,99,235,0.28);
        }

        .title { margin: 0 0 10px; font-size: 20px; line-height: 1.2; text-align: center; }
        .muted { color: var(--muted, #475569); }
        .alert {
          background: #fee2e2; color: #991b1b; border:1px solid #fecaca;
          padding: 8px 10px; border-radius: 8px; margin: 8px 0;
        }

        .form { display: grid; gap: 10px; margin-top: 6px; }
        .field { display: flex; flex-direction: column; gap: 6px; }
        .label { font-size: 12px; color: var(--muted); }

        .input {
          width: 100%; height: 40px; border-radius: 10px; border: 1px solid var(--hairline, #e5e7eb);
          background: var(--bg, #fff); color: var(--text, #0f172a); padding: 0 12px;
        }

        .pwd { position: relative; }
        .pwd .input { padding-right: 40px; }
        .eye {
          position: absolute; top: 50%; right: 6px; transform: translateY(-50%);
          background: transparent; border: none; cursor: pointer; font-size: 16px; line-height: 1;
        }
        .eye:disabled { opacity: .6; cursor:not-allowed; }

        .inline-err { color: #b91c1c; font-size: 12px; margin-top: 6px; }

        .btn.primary {
          margin-top: 6px;
          width: 100%; height: 40px; border-radius: 10px; border: 0;
          background: var(--link, #2563eb); color: #fff; font-weight: 600; cursor: pointer;
        }
        .btn.primary:disabled { opacity: .7; cursor: not-allowed; }

        /* keep small-screen safety identical to Login */
        html, body, #root { max-width: 100%; overflow-x: hidden; }
      `}</style>
    </div>
  );
}

/* ---------- Strength meter ---------- */
function StrengthMeter({ score, label }) {
  const widths = ['8%','25%','50%','75%','100%'];
  const colors = ['#ef4444','#f97316','#a855f7','#3b82f6','#10b981'];
  const idx = Math.max(0, Math.min(4, score));
  return (
    <div className="meter" style={{ marginTop: 6 }}>
      <div className="bar" style={{
        height: 8, borderRadius: 999,
        background: 'color-mix(in srgb, var(--bg-elev,#fff) 70%, var(--hairline,#e5e7eb) 30%)',
        position: 'relative', overflow: 'hidden', border: '1px solid var(--hairline,#e5e7eb)'
      }}>
        <i style={{
          position: 'absolute', inset: 0, width: widths[idx],
          background: colors[idx], display: 'block', borderRadius: 999,
          transition: 'width .35s ease, background .25s ease'
        }} />
      </div>
      <div className="meter-label" style={{ fontSize: 12, color: 'var(--muted,#475569)', marginTop: 6 }}>
        Strength: {label}
      </div>
    </div>
  );
}
